import { Alert, Box, Checkbox, FormControlLabel, InputAdornment, Stack, TextField } from '@mui/material'
import { Modal } from '../../customComponents/Modal'
import { type SyntheticEvent, useEffect, useState } from 'react'
import { type FieldError } from '../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import type { AddOrEditAnnualPlanMeetingRequest } from '../../../models/requestObjects/AnnualPlanRequestObjects'
import { LocationSuggestBox } from '../../customComponents/bingmaps/LocationSuggestBox'
import { type SelectOption } from '@oaisd/michdev.components.react'
import type { Address } from '../../../models/Address'
import MUIAutoComplete from '../../customComponents/MUIAutoComplete'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import { DatePickerWithLabel } from '../../customComponents/DatePickerWithLabel'
import { RemoveOffset, SetTimeWithoutChangingDate } from '../../../utils/dateUtils'
import { ActivityEditSession } from './priorityActivities/ActivityEditSession'
import { PlanOrganizationTaskName } from '../../../models/PlanOrganizationTask'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { AnnualPlanMeeting } from '../../../models/AnnualPlanMeeting'
import { AddMeeting, UpdateMeeting } from '../../../services/MeetingScheduleService'

interface Props {
  meeting?: AnnualPlanMeeting
  teamMembers: AnnualPlanTeamMember[]
  isImplementationPhase?: boolean
  onSuccess: () => Promise<void>
  onCancel: () => void
  orgId: number
  annualPlanId: number
}

interface AddMeetingFormValues {
  title: string
  description: string
  virtualLink: string
  budgetAmount: string
}

export function AddMeetingModal (props: Props): JSX.Element {
  const emptyAddress: Address = {
    id: 0,
    street: '',
    city: '',
    state: '',
    zip: '',
    apartment: undefined
  }

  const editMeeting = props.meeting
  const isImplementationPhase = props.isImplementationPhase ?? false

  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [contactOptions, setContactOptions] = useState<Array<{ id: string | number, name: string }>>([])
  const [selectedPrimaryContact, setSelectedPrimaryContact] = useState<SelectOption<number> | null>()
  const [isLoading, setIsLoading] = useState(true)
  const [isVirtualMeeting, setIsVirtualMeeting] = useState(editMeeting?.isVirtual ?? false)
  const [meetingDate, setMeetingDate] = useState<Date | null>(editMeeting?.meetingDate ?? null)
  const [meetingTime, setMeetingTime] = useState<Dayjs | null>(editMeeting?.meetingDate == null ? null : dayjs(editMeeting.meetingDate))
  const [endMeetingTime, setEndMeetingTime] = useState<Dayjs | null>(editMeeting?.meetingEndDate == null ? null : dayjs(editMeeting.meetingEndDate))
  const [selectedLocation, setSelectedLocation] = useState<Address>()
  const [editingAddress, setEditingAddress] = useState<Address>(emptyAddress)

  const [formValues, setFormValues] = useState<AddMeetingFormValues>({
    title: editMeeting?.title ?? '',
    description: editMeeting?.description ?? '',
    virtualLink: editMeeting?.virtualLink ?? '',
    budgetAmount: editMeeting != null ? (editMeeting.adjustedDollarAmount?.toString() ?? editMeeting.dollarAmount?.toString() ?? '') : ''
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const teamOptions: Array<{ id: string | number, name: string }> = []
      props.teamMembers.forEach(tm => {
        if (tm.annualPlanRole === AnnualPlanRole.PlanAdministrator || tm.annualPlanRole === AnnualPlanRole.PlanContributor) {
          teamOptions.push({ id: tm.userId, name: `${tm.firstName} ${tm.lastName}` })
        }
      })
      setContactOptions(teamOptions)
      if (props.meeting != null) {
        setEditingAddress(props.meeting.address ?? emptyAddress)
        setSelectedLocation(props.meeting.address)
        const primaryContact: number = Number(props.meeting.primaryContact?.id)
        const selected = teamOptions.find(filt => filt.id === primaryContact)
        if (selected != null) {
          setSelectedPrimaryContact({ id: Number(selected.id), name: selected.name })
        }
      }
      setIsLoading(false)
    }

    void fetchData()
  }, [props.meeting])

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      return
    }

    const loc = selectedLocation ?? emptyAddress
    if (selectedLocation?.city === '' && selectedLocation?.street === '' && editingAddress.street !== '') {
      loc.street = editingAddress.street
      loc.id = 0
    }

    const request: AddOrEditAnnualPlanMeetingRequest = {
      annualPlanId: props.annualPlanId,
      orgId: props.orgId,
      rcnOperationId: props.meeting?.rcnOperationId ?? 0,
      title: formValues.title,
      description: formValues.description,
      meetingDate: meetingDate ?? undefined,
      meetingEndDate: null,
      location: loc,
      primaryContactId: selectedPrimaryContact?.id,
      budgetRequestAmount: (editMeeting != null) ? (props.meeting?.dollarAmount?.toString() ?? '') : formValues.budgetAmount,
      adjustedBudgetAmount: isImplementationPhase ? formValues.budgetAmount : undefined,
      actualBudgetAmount: props.meeting?.actualDollarAmount?.toString(),
      isVirtual: isVirtualMeeting,
      virtualLink: formValues.virtualLink,
      isUpdate: props.meeting?.isUpdate ?? false,
      isImplementationPhase,
      id: props.meeting?.id,
      wasCompleted: props.meeting?.wasCompleted,
      meetingScheduleId: props.meeting?.meetingScheduleId
    }

    const updatedForm = prepareMeetingDates(request, meetingTime, endMeetingTime)

    const { success, errors } = editMeeting != null
      ? await UpdateMeeting(updatedForm)
      : await AddMeeting(updatedForm)

    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    await props.onSuccess()
  }

  const handleSetEndDate = (date: Dayjs | null): void => {
    let meetingDateTime = meetingDate != null ? new Date(meetingDate) : new Date()
    if (date != null) {
      meetingDateTime = SetTimeWithoutChangingDate(meetingDateTime, date.toDate())
      setEndMeetingTime(dayjs(meetingDateTime))
    } else {
      setEndMeetingTime(null)
    }
  }

  const prepareMeetingDates = (
    form: AddOrEditAnnualPlanMeetingRequest,
    meetingTime: Dayjs | null,
    endMeetingTime: Dayjs | null
  ): AddOrEditAnnualPlanMeetingRequest => {
    const updatedForm = { ...form }

    let meetingDate = new Date(updatedForm.meetingDate ?? new Date())
    if (meetingTime != null) {
      meetingDate = SetTimeWithoutChangingDate(meetingDate, meetingTime.toDate())
      updatedForm.meetingDate = RemoveOffset(meetingDate)
    }
    if (endMeetingTime != null) {
      meetingDate = SetTimeWithoutChangingDate(meetingDate, endMeetingTime.toDate())
      updatedForm.meetingEndDate = RemoveOffset(meetingDate)
    } else {
      updatedForm.meetingEndDate = null
    }

    return updatedForm
  }

  function validateForm (): boolean {
    const formFieldErrors: FieldError[] = []

    let invalidDate: boolean = false
    if (meetingTime != null && meetingDate != null) {
      let meetingDateTime = new Date(meetingDate)
      meetingDateTime = SetTimeWithoutChangingDate(meetingDateTime, meetingTime.toDate())
      const nowDate = new Date()
      if (meetingDateTime < nowDate && !isImplementationPhase) {
        invalidDate = true
      }
    }

    if (formValues.title === '') {
      formFieldErrors.push({ fieldName: 'title', message: '' })
    }

    if (meetingDate == null) {
      formFieldErrors.push({ fieldName: 'meetingDate', message: '' })
    }

    if (meetingTime == null) {
      formFieldErrors.push({ fieldName: 'meetingTime', message: '' })
    }

    if (endMeetingTime?.isBefore(meetingTime) === true) {
      formFieldErrors.push({ fieldName: 'endMeetingTime', message: 'End time must be after start time.' })
    }

    if (!isVirtualMeeting) {
      if (selectedLocation == null || (selectedLocation.street === '' && selectedLocation.zip === '')) {
        if (editingAddress.street === '') {
          formFieldErrors.push({ fieldName: 'address', message: '' })
        }
      }
    }

    if (selectedPrimaryContact == null) {
      formFieldErrors.push({ fieldName: 'primaryContactId', message: '' })
    }

    if (formValues.budgetAmount === '' && (props.isImplementationPhase ?? false)) {
      formFieldErrors.push({ fieldName: 'budgetRequestAmount', message: '' })
    }

    setFieldErrors(formFieldErrors)
    if (formFieldErrors.length > 0 || invalidDate) {
      if (invalidDate) {
        toast.error('Meeting must be in the future')
      }
      if (formFieldErrors.length > 0) {
        toast.error('Please complete all required fields.')
      }
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target

    if (name === 'budgetRequestAmount') {
      setFormValues({ ...formValues, budgetAmount: value })
    } else if (name in formValues) {
      setFormValues({ ...formValues, [name]: value })
    }

    setFieldErrors(fieldErrors.filter(f => f.fieldName !== name))
  }

  const handleContactChange = (e: SyntheticEvent, value: { id: string | number, name: string } | null): void => {
    const contact = contactOptions.find(d => d.id === value?.id) ?? null

    if (contact == null) {
      return
    }

    setSelectedPrimaryContact({ id: Number(contact.id), name: contact.name })
  }

  const handlePlaceSelected = (address: Address): void => {
    setEditingAddress(address)
    setSelectedLocation(address)
  }

  const handleLocChange = (e: any): void => {
    if (e.target.value === '') {
      setSelectedLocation(emptyAddress)
      setEditingAddress(emptyAddress)
    } else {
      setEditingAddress({ ...editingAddress, street: e.target.value })
    }
  }

  function handleDateChanged (newValue: Date | null): void {
    // Update meetingTime and endMeetingTime to the new date with existing times
    if (newValue !== null) {
      if (meetingTime !== null) {
        setMeetingTime(dayjs(newValue).hour(meetingTime.hour()).minute(meetingTime.minute()))
      }
      if (endMeetingTime !== null) {
        setEndMeetingTime(dayjs(newValue).hour(endMeetingTime.hour()).minute(endMeetingTime.minute()))
      }
    }

    setMeetingDate(newValue)
  }

  const handleVirtualChecked = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setIsVirtualMeeting(e.target.checked)

    if (e.target.checked) {
      setFieldErrors(fieldErrors.filter(f => f.fieldName !== 'address'))
    } else {
      setFormValues({ ...formValues, virtualLink: '' })
    }
  }

  const content = <Box minWidth='200px'>
    {
      props.isImplementationPhase === true && props.meeting == null &&
        <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
          backgroundColor: 'var(--blue-500)',
          color: 'white',
          mb: '.5em',
          fill: 'white'
        }} severity="info"
        >The addition of this item will prompt a review from the START team.
        </Alert>
    }
    <TextField
      variant='filled'
      data-testid='meetingTitle'
      fullWidth
      value={formValues.title}
      onChange={handleFieldUpdate}
      placeholder='Name of the meeting'
      label={'Meeting Title'}
      name={'title'}
      required
      error={fieldErrors.some((error) => error.fieldName === 'title')}
    />

    <TextField
      variant='filled'
      data-testid='meetingDescription'
      fullWidth
      multiline
      value={formValues.description}
      minRows={3}
      name='description'
      label='Meeting Description'
      onChange={handleFieldUpdate}
      sx={{ mt: '.75em' }}
    />

    <DatePickerWithLabel
      disablePast={!isImplementationPhase}
      FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
      name='meetingDate'
      label='Meeting Date'
      required
      error={fieldErrors.some((error) => error.fieldName === 'meetingDate')}
      dataTestId='meetingDate'
      onChange={handleDateChanged}
      sx={{ mt: '.75em' }}
      value={meetingDate ?? null}
    />

    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction='row' gap='1em'>
        <TimePicker
          sx={{ mt: '.75em', width: '225px' }}
          slotProps={{
            textField: {
              name: 'meetingTime',
              required: true,
              error: fieldErrors.some((error) => error.fieldName === 'meetingTime'),
              variant: 'filled',
              inputProps: {
                'data-testid': 'meetingTime'
              }
            }
          }}
          label='Start Meeting Time'
          value={meetingTime}
          onChange={(newValue) => { setMeetingTime(newValue) }}
        />

        <TimePicker
          sx={{ mt: '.75em', width: '225px' }}
          slotProps={{
            textField: {
              name: 'endMeetingTime',
              required: false,
              helperText: fieldErrors.find(f => f.fieldName === 'endMeetingTime')?.message,
              error: fieldErrors.some((error) => error.fieldName === 'endMeetingTime'),
              variant: 'filled',
              inputProps: {
                'data-testid': 'endMeetingTime'
              }
            }
          }}
          label='End Meeting Time'
          value={endMeetingTime}
          onChange={(newValue) => { handleSetEndDate(newValue) }}
        />
      </Stack>
    </LocalizationProvider>

    <Box>
      <FormControlLabel
        label={'Virtual Meeting?'}
        sx={{ mt: '.75em', width: '225px' }}
        control={
          <Checkbox
            sx={{ color: 'var(--grey-700)' }}
            checked={isVirtualMeeting}
            onChange={handleVirtualChecked}
            data-testid='isVirtualCheckbox'
          />
        }
      />
    </Box>

    { isVirtualMeeting && <TextField
      variant='filled'
      data-testid='virtualLink'
      fullWidth
      value={formValues.virtualLink}
      onChange={handleFieldUpdate}
      label={'Virtual Link'}
      name={'virtualLink'}
    />
    }

    <Box sx={{ my: '.75em' }}>
      <LocationSuggestBox editData={editingAddress}
        integratedLabel={true}
        error={fieldErrors.some((error) => error.fieldName === 'address')}
        required={!isVirtualMeeting}
        onChange={handleLocChange}
        onPlaceSelected={handlePlaceSelected}
      />
    </Box>

    <MUIAutoComplete
      required
      label='Primary Contact'
      name='memberSelect'
      value={selectedPrimaryContact ?? undefined}
      onChange={handleContactChange}
      options={contactOptions}
      error={fieldErrors.some((error) => error.fieldName === 'primaryContactId')}
      sx={{ width: '100%' }}
    />

    <TextField
      sx={{ mt: '.75em', width: '250px' }}
      variant='filled'
      onChange={handleFieldUpdate}
      label={'Budget Amount'}
      data-testid='budgetRequestAmount'
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      name={'budgetRequestAmount'}
      required
      error={fieldErrors.some((error) => error.fieldName === 'budgetRequestAmount')}
      value={formValues.budgetAmount}
    />

    {props.meeting != null &&
      <ActivityEditSession
        activityId={props.meeting.id ?? 0}
        activityName={PlanOrganizationTaskName.RcnOperations}
        onClose={() => { props.onCancel() }}
      />
    }
  </Box>

  if (isLoading) {
    return <></>
  }

  return <Modal
    open={true}
    maxWidth='md'
    title={props.meeting != null ? (isImplementationPhase ? 'Update Meeting' : 'Edit Meeting') : 'Add Meeting'}
    confirmButtonText='Save'
    bodyContent={content}
    onClose={() => { props.onCancel() }}
    onConfirm={handleSave}
  />
}
